import { supabase } from "@/integrations/supabase/client";
import type { ArticleRow, ContentBrief, ArticleRevision, PublishCheckResult } from "./types";

export async function fetchArticles(): Promise<ArticleRow[]> {
  const { data, error } = await supabase
    .from("blog_articles")
    .select("*")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as ArticleRow[];
}

export async function updateArticle(id: string, patch: Partial<ArticleRow>) {
  const { data, error } = await supabase.from("blog_articles").update(patch as never).eq("id", id).select().single();
  if (error) throw error;
  return data as unknown as ArticleRow;
}

export async function createArticle(input: { title: string; slug: string; category: string }) {
  const { data, error } = await supabase
    .from("blog_articles")
    .insert({
      title: input.title,
      slug: input.slug,
      category: input.category,
      excerpt: "",
      summary: "",
      seo_title: input.title,
      meta_description: "",
      status: "idea",
    } as never)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ArticleRow;
}

export async function deleteArticle(id: string) {
  const { error } = await supabase.from("blog_articles").delete().eq("id", id);
  if (error) throw error;
}

export async function fetchBrief(article_id: string): Promise<ContentBrief | null> {
  const { data, error } = await supabase
    .from("content_briefs")
    .select("*")
    .eq("article_id", article_id)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as ContentBrief) ?? null;
}

export async function fetchRevisions(article_id: string): Promise<ArticleRevision[]> {
  const { data, error } = await supabase
    .from("article_revisions")
    .select("*")
    .eq("article_id", article_id)
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data ?? []) as unknown as ArticleRevision[];
}

export async function fetchEmbeddingJob(article_id: string) {
  const { data } = await supabase
    .from("embedding_jobs" as never)
    .select("id, status, reason, attempts, last_error, created_at, finished_at")
    .eq("article_id", article_id)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data as null | {
    id: string; status: string; reason: string; attempts: number;
    last_error: string | null; created_at: string; finished_at: string | null;
  };
}

export async function fetchPublishAudit(article_id: string) {
  const { data } = await supabase
    .from("publish_audit" as never)
    .select("id, action, reason, meta, created_at")
    .eq("article_id", article_id)
    .order("created_at", { ascending: false })
    .limit(20);
  return (data ?? []) as Array<{ id: string; action: string; reason: string | null; meta: Record<string, unknown>; created_at: string }>;
}

export async function invokeFn<T>(name: string, body: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    // Surface the function's own readable message instead of a generic status error
    let message = error.message;
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const payload = await ctx.clone().json();
        if (payload?.error) message = String(payload.error);
      } catch { /* keep default message */ }
    }
    throw new Error(message);
  }
  if (data && typeof data === "object" && "error" in data && (data as { error?: string }).error) {
    throw new Error((data as { error: string }).error);
  }
  return data as T;
}

export const generateBrief = (input: { article_id: string; topic: string; target_keywords?: string[]; persona?: string; cluster_name?: string; notes?: string }) =>
  invokeFn<{ brief: ContentBrief; suggestion: unknown }>("content-brief", input);

export const generateDraft = (article_id: string, mode: "draft" | "regenerate" = "draft") =>
  invokeFn<{ ok: boolean; model: string; confidence?: number }>("content-draft", { article_id, mode });

export const runPublishCheck = (article_id: string) =>
  invokeFn<PublishCheckResult>("publish-check", { article_id });

export const embedArticle = (article_id: string, force = false) =>
  invokeFn<{ ok?: boolean; skipped?: boolean; reason?: string }>("article-embed", { article_id, force });

export const suggestInternalLinks = (article_id: string, limit = 6) =>
  invokeFn<{ suggestions: { article_id: string; slug: string; title: string; similarity: number }[] }>(
    "internal-link-suggest", { article_id, limit });

export interface AnchorCandidate {
  slug: string;
  title: string;
  similarity: number;
  section_index: number;
  paragraph_index: number;
  phrase: string;
  char_start: number;
  char_end: number;
  paragraph_hash: string;
  paragraph_preview: string;
}

export const fetchAnchorCandidates = (article_id: string, limit = 8) =>
  invokeFn<{
    suggestions: { article_id: string; slug: string; title: string; similarity: number }[];
    anchor_candidates: AnchorCandidate[];
    reason?: string;
  }>("internal-link-suggest", { article_id, limit, action: "anchor_candidates" });

export const applyAnchorSuggestion = (input: { article_id: string } & AnchorCandidate) =>
  invokeFn<{ ok: boolean; reason?: string; message?: string; article?: unknown }>(
    "internal-link-suggest", { action: "apply_anchor", ...input });

export const autoApplyInternalLinks = (article_id: string, limit = 8) =>
  invokeFn<{ ok: boolean; applied: number; skipped: { slug: string; reason: string }[]; reason?: string }>(
    "internal-link-suggest", { article_id, limit, action: "auto_apply" });


export type EditorialTool =
  | "rewrite_intro" | "humanize_section" | "eeat_enhance"
  | "tighten_cta"   | "snippet_optimize"
  | "title_alternatives" | "meta_optimize"
  | "regenerate_section";

export const proposeEditorial = (input: { article_id: string; tool: EditorialTool; section_index?: number; extra?: string }) =>
  invokeFn<{ ok: true; tool: EditorialTool; model: string; section_index: number | null; proposal: Record<string, unknown> }>(
    "editorial-tool", input);

export const generateSeoKeywords = (input: { article_id?: string; topic?: string; seed_keywords?: string[]; persona?: string }) =>
  invokeFn<{
    ok: true; model: string; provider: string;
    primary_keyword: string;
    secondary_keywords: string[];
    long_tail_keywords: string[];
    notes: string | null;
  }>("generate-seo-keywords", input);

export const applyEditorial = (input: { article_id: string; patch: Record<string, unknown>; revision_type?: string; source_tool?: string; ai_model?: string }) =>
  invokeFn<{ ok: true; article: unknown }>("editorial-tool", { action: "apply", ...input });

export const drainEmbeddingQueue = () =>
  invokeFn<{ ok: true; claimed: number; done: number; skipped: number; errors: number }>("process-embeddings", {});

export async function updateBriefStatus(
  brief_id: string,
  status: "draft" | "in_review" | "approved" | "rejected",
  reviewer_notes?: string | null
) {
  const { data: userResp } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("content_briefs")
    .update({
      status,
      reviewer_notes: reviewer_notes ?? null,
      reviewed_at: new Date().toISOString(),
      reviewed_by: userResp?.user?.id ?? null,
    } as never)
    .eq("id", brief_id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ContentBrief;
}

export async function updateBrief(brief_id: string, patch: Partial<ContentBrief>) {
  const { data, error } = await supabase
    .from("content_briefs")
    .update(patch as never)
    .eq("id", brief_id)
    .select()
    .single();
  if (error) throw error;
  return data as unknown as ContentBrief;
}

export interface BriefQueueRow {
  brief: ContentBrief;
  article: Pick<ArticleRow, "id" | "title" | "slug" | "category" | "status" | "updated_at">;
}

export async function fetchBriefQueue(): Promise<BriefQueueRow[]> {
  const { data, error } = await supabase
    .from("content_briefs")
    .select("*, article:blog_articles!inner(id,title,slug,category,status,updated_at)")
    .order("updated_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return ((data ?? []) as unknown as (ContentBrief & { article: BriefQueueRow["article"] })[])
    .map((r) => {
      const { article, ...brief } = r;
      return { brief: brief as ContentBrief, article };
    });
}
